class Ripple {
  constructor(x, y, color) {
    this.x = x || 0;
    this.y = y || 0;
    this.radius = 0;
    this.maxRadius = 85; // 增加最大半径
    this.alpha = 1.0; // 增加初始透明度
    // 确保color对象的属性都是有效的数字
    this.color = {
      r: Math.min(255, Math.max(0, parseInt(color?.r) || 0)),
      g: Math.min(255, Math.max(0, parseInt(color?.g) || 0)),
      b: Math.min(255, Math.max(0, parseInt(color?.b) || 0))
    };
    this.speed = 1.5; // 扩散速度
  }

  update() {
    if (this.radius < this.maxRadius) {
      this.radius += this.speed;

      // 确保alpha值在有效范围内
      const progress = 1 - (this.radius / this.maxRadius);
      // 使用更平滑的渐变曲线
      this.alpha = Math.max(0, Math.min(1.0, Math.pow(progress, 0.5)));
      return true;
    }
    return false;
  }

  draw(ctx) {
    ctx.save();

    // 确保所有值都是有效的数字
    const innerAlpha = Math.max(0, Math.min(1, parseFloat(this.alpha * 0.5) || 0));
    const outerAlpha = 0; // 外圈始终完全透明

    // 确保颜色值都是有效的整数
    const r = Math.min(255, Math.max(0, Math.round(this.color.r)));
    const g = Math.min(255, Math.max(0, Math.round(this.color.g)));
    const b = Math.min(255, Math.max(0, Math.round(this.color.b)));

    const gradient = ctx.createRadialGradient(
      this.x, this.y, this.radius * 0.7,
      this.x, this.y, this.radius
    );

    // 使用处理过的安全值创建颜色字符串
    gradient.addColorStop(0, `rgba(${r}, ${g}, ${b}, ${innerAlpha})`);
    gradient.addColorStop(1, `rgba(${r}, ${g}, ${b}, ${outerAlpha})`);

    // 只绘制渐变填充，不再绘制边框
    ctx.beginPath();
    ctx.arc(this.x, this.y, this.radius, 0, Math.PI * 2);
    ctx.fillStyle = gradient;
    ctx.fill();
    ctx.restore();
  }
}

export default class StarEffect {
  constructor(options = {}) {
    this.options = {
      canvasId: options.canvasId || 'starCanvas',
      starCount: options.starCount || 12, // 增加星星数量
      ...options
    };

    this.canvas = null;
    this.ctx = null;
    this.stars = [];
    this.ripples = [];
    this.animationFrame = null;

    this.initCanvas = this.initCanvas.bind(this);
    this.resizeCanvas = this.resizeCanvas.bind(this);
    this.handleClick = this.handleClick.bind(this);
    this.createStar = this.createStar.bind(this);
    this.createStarPath = this.createStarPath.bind(this);
    this.animate = this.animate.bind(this);
    this.stopAnimation = this.stopAnimation.bind(this);
  }


  initCanvas() {
    this.canvas = document.getElementById(this.options.canvasId);
    if (!this.canvas) {
      console.error(`Canvas with id "${this.options.canvasId}" not found`);
      return;
    }
    this.ctx = this.canvas.getContext('2d');
    this.resizeCanvas();
    window.addEventListener('resize', this.resizeCanvas);
    // this.canvas.addEventListener('click', this.handleClick); // 隐藏点击
    this.animationFrame = requestAnimationFrame(() => this.animate());
  }

  resizeCanvas() {
    if (!this.canvas) return;
    const live2dBox = document.getElementsByClassName('live2d')[0];
    this.canvas.width = live2dBox.clientWidth;
    this.canvas.height = live2dBox.clientHeight;
  }

  handleClick(event) {
    const rect = this.canvas.getBoundingClientRect();
    const centerX = event.clientX - rect.left;
    const centerY = event.clientY - rect.top;

    const startColor = this.hexToRgb('#6eb2ff'); // 主要蓝色
    const endColor = this.hexToRgb('#a7ceff'); // 更亮的蓝色
    const progress = Math.random() * 0.7 + 0.3; // 确保颜色更偏向明亮
    const color = {
      r: Math.floor(startColor.r + (endColor.r - startColor.r) * progress),
      g: Math.floor(startColor.g + (endColor.g - startColor.g) * progress),
      b: Math.floor(startColor.b + (endColor.b - startColor.b) * progress)
    };

    const ripple = new Ripple(centerX, centerY, color);
    this.ripples.push(ripple);

    // 创建向四周炸开的星星
    for (let i = 0; i < this.options.starCount; i++) {
      const angle = (Math.PI * 2 * i) / this.options.starCount + Math.random() * 0.5;
      const star = this.createStar(centerX, centerY, color);
      star.angle = angle;
      star.speed = 0.4 + Math.random() * 0.3; // 降低星星速度，范围在0.4-0.7之间
      star.maxDistance = ripple.maxRadius * 0.9; // 确保星星在光圈范围内
      star.ripple = ripple;
    }
  }

  createStar(x, y, color) {
    const star = {
      x: x,
      y: y,
      startX: x,
      startY: y,
      size: 3 + Math.random() * 3, // 调整星星大小范围
      angle: 0,
      speed: 0,
      rotationSpeed: (Math.random() - 0.5) * 0.2,
      rotation: Math.random() * Math.PI * 2,
      alpha: 1,
      color: color,
      maxDistance: 0
    };

    this.stars.push(star);
    return star;
  }

  createStarPath(ctx, x, y, size, rotation) {
    const spikes = 5;
    const outerRadius = size;
    const innerRadius = size * 0.4;

    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(rotation);

    ctx.beginPath();
    for (let i = 0; i < spikes * 2; i++) {
      const radius = i % 2 === 0 ? outerRadius : innerRadius;
      const angle = (Math.PI / spikes) * i;
      const pointX = Math.cos(angle) * radius;
      const pointY = Math.sin(angle) * radius;

      if (i === 0) {
        ctx.moveTo(pointX, pointY);
      } else {
        ctx.lineTo(pointX, pointY);
      }
    }
    ctx.closePath();
    ctx.restore();
  }

  hexToRgb(hex) {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null;
  }

  animate() {
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

    // 绘制光圈
    for (let i = this.ripples.length - 1; i >= 0; i--) {
      const ripple = this.ripples[i];
      if (!ripple.update()) {
        this.ripples.splice(i, 1);
        continue;
      }
      ripple.draw(this.ctx);
    }

    // 绘制并更新星星
    for (let i = this.stars.length - 1; i >= 0; i--) {
      const star = this.stars[i];

      // 计算当前距离
      const dx = star.x - star.startX;
      const dy = star.y - star.startY;
      const distance = Math.sqrt(dx * dx + dy * dy);

      // 更新星星位置
      if (distance < star.maxDistance) {
        star.x += Math.cos(star.angle) * star.speed;
        star.y += Math.sin(star.angle) * star.speed;

        // 根据距离计算透明度，使用更平滑的渐变
        star.alpha = Math.max(0, Math.pow(1 - (distance / star.maxDistance), 0.8));

        // 添加轻微的上浮效果
        star.y -= 0.15;

        // 更新旋转
        star.rotation += star.rotationSpeed;

        // 绘制星星
        this.ctx.save();
        this.ctx.globalAlpha = star.alpha;

        // 设置发光效果
        this.ctx.shadowColor = `rgba(${star.color.r}, ${star.color.g}, ${star.color.b}, 0.5)`;
        this.ctx.shadowBlur = 8; // 增强发光效果

        // 绘制星星轮廓
        this.ctx.strokeStyle = `rgba(${star.color.r}, ${star.color.g}, ${star.color.b}, ${star.alpha})`;
        this.ctx.lineWidth = 1.2; // 调整线条宽度，使星星更精致
        this.createStarPath(this.ctx, star.x, star.y, star.size, star.rotation);
        this.ctx.stroke();

        // 填充星星
        this.ctx.fillStyle = `rgba(${star.color.r}, ${star.color.g}, ${star.color.b}, ${star.alpha * 0.6})`; // 增加填充不透明度
        this.ctx.fill();

        this.ctx.restore();
      } else {
        // 如果星星超出最大距离，移除它
        this.stars.splice(i, 1);
      }
    }

    this.animationFrame = requestAnimationFrame(() => this.animate());
  }

  stopAnimation() {
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
    }
  }


  destroy() {
    this.stopAnimation();
    if (this.canvas) {
      this.canvas.removeEventListener('click', this.handleClick);
      window.removeEventListener('resize', this.resizeCanvas);
    }
  }
}
